import type { ReactNode } from 'react';

/**
 * The line icons the module uses, inlined.
 *
 * PrimeIcons is a font and cannot be stroked to match the chart's 1.6px line
 * weight, so these few are drawn instead. They inherit `currentColor` and take
 * their size from the `.ic` class in the stylesheet.
 */
const PATHS: Record<string, ReactNode> = {
  search: (
    <>
      <circle cx="9" cy="9" r="5.5" />
      <path d="M13 13l4 4" />
    </>
  ),
  down: <path d="M5 8l5 5 5-5" />,
  chev: <path d="M8 5l5 5-5 5" />,
  x: <path d="M5.5 5.5l9 9M14.5 5.5l-9 9" />,
  plus: <path d="M10 4.5v11M4.5 10h11" />,
  edit: <path d="M13.5 3.8l2.7 2.7L7.4 15.3l-3.4.7.7-3.4z" />,
  cols: (
    <>
      <rect x="2.5" y="4" width="6" height="12" rx="1.2" />
      <rect x="11.5" y="4" width="6" height="12" rx="1.2" />
    </>
  ),
  refresh: <path d="M16 10a6 6 0 1 1-1.8-4.3M16 3v3.2h-3.2" />,
  out: <path d="M12 5.5V4a1.5 1.5 0 0 0-1.5-1.5h-5A1.5 1.5 0 0 0 4 4v12a1.5 1.5 0 0 0 1.5 1.5h5A1.5 1.5 0 0 0 12 16v-1.5M8.5 10H17m0 0l-2.6-2.6M17 10l-2.6 2.6" />,
  sitemap: (
    <>
      <rect x="7" y="2.5" width="6" height="4" rx="1" />
      <rect x="2" y="13.5" width="5.5" height="4" rx="1" />
      <rect x="12.5" y="13.5" width="5.5" height="4" rx="1" />
      <path d="M10 6.5v4M4.75 13.5v-2h10.5v2" />
    </>
  ),
  building: (
    <>
      <rect x="4" y="3" width="12" height="14" rx="1.4" />
      <path d="M7.5 6.5h2M7.5 9.5h2M7.5 12.5h2M12.5 6.5h1M12.5 9.5h1M12.5 12.5h1" />
    </>
  ),
  expand: (
    <>
      <path d="M8 3H3v5M17 12v5h-5M12 3h5v5M8 17H3v-5" />
    </>
  ),
  collapse: (
    <>
      <path d="M3 8h5V3M17 12h-5v5M12 8h5V3M8 12H3v5" />
    </>
  ),
  alert: (
    <>
      <path d="M10 3.2L18 16.8H2z" />
      <path d="M10 8v3.4M10 14.1v.1" />
    </>
  ),
};

export type IconName = keyof typeof PATHS;

interface IconProps {
  name: IconName;
  className?: string;
  size?: number;
}

const Icon = ({ name, className, size }: IconProps) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    aria-hidden="true"
    focusable="false"
    style={size ? { width: size, height: size } : undefined}
  >
    {PATHS[name]}
  </svg>
);

export default Icon;
